'use client';

import type { InputHTMLAttributes, ReactNode } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import type { ProfileFormInput } from '@eliadmin/core/src/components/nt/account/profile/profile-schema';
import { SelectInput, TextInput } from '@eliadmin/core/src/components/nt/forms/controls';
import { describedBy, FormField } from '@eliadmin/core/src/components/nt/forms/form-field';

type TextName = Exclude<keyof ProfileFormInput, 'same_delivery'>;

/** `profile-<name>` – ids of the controls and their helper / error lines. */
export const fieldId = (name: keyof ProfileFormInput) => `profile-${name.replace(/_/g, '-')}`;

/** Figma „Form / Input“ bound to the profile form: label, the 48 px input, helper or error. */
export function ProfileTextField({
  name,
  label,
  required,
  hint,
  className,
  ...input
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'name' | 'id'> & {
  name: TextName;
  label: string;
  hint?: ReactNode;
}) {
  const { register, getFieldState, formState } = useFormContext<ProfileFormInput>();
  const error = getFieldState(name, formState).error?.message;
  const id = fieldId(name);

  return (
    <FormField
      id={id}
      label={label}
      required={required}
      hint={hint}
      error={error}
      className={className}
    >
      <TextInput
        id={id}
        invalid={!!error}
        aria-required={required || undefined}
        aria-describedby={describedBy(id, { error, hint })}
        {...input}
        {...register(name)}
      />
    </FormField>
  );
}

/** „Krajina“ – the searchable select of the store's countries. */
export function ProfileCountryField({
  name,
  label,
  countries,
  autoComplete,
}: {
  name: 'country' | 'delivery_country';
  label: string;
  countries: Country[];
  autoComplete: string;
}) {
  const { control, getFieldState, formState } = useFormContext<ProfileFormInput>();
  const { field } = useController({ control, name });
  const error = getFieldState(name, formState).error?.message;
  const id = fieldId(name);

  return (
    <FormField id={id} label={label} required error={error}>
      <SelectInput
        id={id}
        invalid={!!error}
        aria-required
        aria-describedby={describedBy(id, { error })}
        autoComplete={autoComplete}
        {...field}
        label={label}
        options={countries.map(country => ({ value: String(country.id), label: country.name }))}
      />
    </FormField>
  );
}
